import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

type CancelBookingDialogProps = {
  onConfirm: (reason: string) => Promise<void> | void;
  disabled?: boolean;
};

const FIELD_CLASS =
  "rounded-2xl resize-none min-h-40 border-border bg-card px-4 text-sm font-medium text-muted-foreground placeholder:text-border";

export function CancelBookingDialog({
  onConfirm,
  disabled,
}: CancelBookingDialogProps) {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate a delay for demonstration purposes
      await onConfirm(reason.trim());
      setOpen(false); // Close the dialog after successful submission
      setReason(""); // Clear the reason after successful submission
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant="default"
            disabled={disabled}
            className="mt-4 w-full cursor-pointer text-sm font-semibold bg-muted-foreground hover:bg-[#242424] md:w-2xs md:mt-0"
          >
            Cancel Booking
          </Button>
        }
      />
      <DialogContent
        className="sm:max-w-xs md:max-w-md"
        showCloseButton={false}
      >
        <DialogClose
          render={
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-3 right-2"
            />
          }
        >
          <X className="size-6" />
          <span className="sr-only">Close</span>
        </DialogClose>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-secondary-foreground">
            Cancel Booking
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label
            htmlFor="cancel-reason"
            className="text-xs font-bold text-field"
          >
            Enter reason <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={6}
            placeholder="Enter reasons"
            className={FIELD_CLASS}
          />
        </div>

        <DialogFooter className="flex-col gap-2 border-t-0 bg-card">
          <Button
            type="button"
            variant="default"
            className="w-full cursor-pointer"
            disabled={reason.trim() === "" || isSubmitting}
            onClick={handleConfirm}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" />
                Submitting...
              </>
            ) : (
              "Cancel Booking"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
